"use client";

import { useTranslations } from "next-intl";
import { baseName } from "../_components/format";
import { OptionRadioGroup } from "../_components/OptionRadioGroup";
import type { MediaOperation, OperationOptionsProps } from "./types";

interface ExtractFrameOpts {
  type: "png" | "jpeg";
}

function ExtractFrameForm({
  options,
  setOptions,
  disabled,
}: OperationOptionsProps<ExtractFrameOpts>) {
  const t = useTranslations("mediaTools.options.extractFrame");
  const types = [
    { value: "png" as const, label: "PNG", hint: t("types.pngHint") },
    { value: "jpeg" as const, label: "JPEG", hint: t("types.jpegHint") },
  ];

  return (
    <div className="space-y-2">
      <OptionRadioGroup
        name="image-type"
        label={t("imageFormatLabel")}
        value={options.type}
        onChange={(type) => setOptions({ type })}
        options={types}
        disabled={disabled}
      />
      <p className="text-sm text-foreground/60">{t("scrubHelp")}</p>
    </div>
  );
}

export const extractFrameOperation: MediaOperation<ExtractFrameOpts> = {
  id: "extract-frame",
  group: "video",
  inputKinds: ["video"],
  outputKind: "image",
  showSourcePreview: true,
  defaultOptions: { type: "png" },
  OptionsForm: ExtractFrameForm,
  run: async (ctx, options) => {
    const el = ctx.getPreviewElement?.();
    if (!(el instanceof HTMLVideoElement) || el.videoWidth === 0) {
      throw new Error("Load a video and let it load before capturing.");
    }
    const canvas = document.createElement("canvas");
    canvas.width = el.videoWidth;
    canvas.height = el.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas is not available in this browser.");
    context.drawImage(el, 0, 0);

    const mimeType = options.type === "jpeg" ? "image/jpeg" : "image/png";
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, mimeType, 0.95)
    );
    if (!blob) throw new Error("Could not capture the frame.");

    const stamp = el.currentTime.toFixed(1);
    return {
      blob,
      baseName: `${baseName(ctx.file?.name ?? "frame")}-${stamp}s`,
      extension: options.type === "jpeg" ? "jpg" : "png",
    };
  },
};
